// src/pages/Dashboard/CreateProject.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../Stylesheets/Dashboard/CreateProject.css';
import { FaChevronRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import SuccessImage from '../../../assets/illustration/successIcon.png';
import imageCompression from 'browser-image-compression';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';
import { useDispatch } from 'react-redux';
import { createProject } from '../../../Redux/Projects/projectSlice';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { fetchOverview } from '../../../Redux/Projects/overviewSlice';
import AlertBox from '../../../Components/Common/AlertBox';


const CreateProject = () => {




  const [step, setStep] = useState(1);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    client: '',
    location: '',
    startDate: '',
    endDate: '',
    budget: '',
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [errors, setErrors] = useState({});
  const [newProjectId, setNewProjectId] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);   // ⬅️ NEW
  const [submitError, setSubmitError] = useState('');

  // ✅ NEW: local custom alert for step1 upload/auth errors etc.
  const [localAlert, setLocalAlert] = useState({ type: 'error', message: '' });
  const setAlert = (message, type = 'error') => setLocalAlert({ type, message });
  const clearAlert = () => setLocalAlert({ type: 'error', message: '' });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const todayISO = new Date().toISOString().slice(0, 10);

  const prettyDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const addMonthsToISO = (baseIso, months) => {
    const d = new Date(baseIso);
    d.setMonth(d.getMonth() + months);
    return d.toISOString().slice(0, 10);
  };


  const handleChange = (e) => {

    if (localAlert.message) clearAlert();


    const { name, value } = e.target;

    // normal fields
    if (name !== 'startDate' && name !== 'endDate') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    // date fields – keep endDate >= startDate automatically
    if (name === 'startDate') {
      setFormData((prev) => {
        const next = { ...prev, startDate: value };
        if (prev.endDate && new Date(prev.endDate) < new Date(value)) {
          next.endDate = value; // auto-fix range
        }
        return next;
      });
    }

    if (name === 'endDate') {
      setFormData((prev) => ({ ...prev, endDate: value }));
    }
  };


  const handleQuickDuration = (months) => {
    if (localAlert.message) clearAlert();


    const base = formData.startDate || todayISO;
    const end = addMonthsToISO(base, months);

    setFormData((prev) => ({
      ...prev,
      startDate: base,
      endDate: end,
    }));

    // clear any previous date errors
    setErrors((prev) => ({ ...prev, startDate: '', endDate: '' }));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!formData.name) errs.name = 'Project name is required';
    if (!formData.client) errs.client = 'Client name is required';
    if (!formData.location) errs.location = 'Location is required';
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!formData.startDate) errs.startDate = 'Start date is required';
    if (!formData.endDate) errs.endDate = 'End date is required';
    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) > new Date(formData.endDate)
    ) {
      errs.endDate = 'End date must be after start date';
    }
    if (!formData.budget || isNaN(formData.budget)) {
      errs.budget = 'Valid budget is required';
    }
    return errs;
  };

  const handleNext = () => {
    const errs = validateStep1();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setAlert('Fix the highlighted fields to continue.', 'error');
      return;
    }
    setErrors({});
    clearAlert();
    setStep(2);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const user = getAuth().currentUser;
    if (!user) {
      setAlert('Please sign in first to upload a project image.', 'error');
      return;
    }

    try {
      setIsUploadingImage(true);
      setUploadProgress(0);
      setUploadStatusText('Preparing image...');
      clearAlert();

      // 1) Compress
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });

      setUploadStatusText('Uploading to cloud...');

      // 2) Upload with progress
      const path = `project_thumbnails/project_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);

      const uploadTask = uploadBytesResumable(storageRef, compressed);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          console.error('🔥 Upload failed:', error.message || error);
          setIsUploadingImage(false);
          setUploadStatusText('Upload failed');
          setUploadProgress(0);
          setAlert('Failed to upload image. Please try again.', 'error');
        },
        async () => {
          // 3) Done, get URL
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          setThumbnail(url);
          console.log('✅ Uploaded:', url);
          setIsUploadingImage(false);
          setUploadStatusText('');
          setUploadProgress(100);
          setAlert('Image uploaded successfully.', 'success');
        }
      );
    } catch (err) {
      console.error('🔥 Upload failed:', err.message || err);
      setIsUploadingImage(false);
      setUploadStatusText('Upload failed');
      setUploadProgress(0);
      setAlert('Failed to upload image. Please try again.', 'error');
    }
  };



  const saveProject = () => {
    const errs = validateStep2();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setAlert('Fix the highlighted fields before creating the project.', 'error');
      return;
    }

    const payload = {
      ...formData,
      image: thumbnail || '',
    };
    clearAlert();
    setSubmitError('');
    setIsSubmitting(true);   // 🔴 important

    dispatch(createProject(payload))
      .unwrap()
      .then((created) => {
        setNewProjectId(created._id);
        // 🔁 refresh overview cache so dashboard sees the new project
        dispatch(fetchOverview({ limitProjects: 4, limitActivity: 10 }));
        setStep(3);
        try {
          toast.success('Project created successfully');
        } catch (_) { }
      })
      .catch((err) => {
        console.error('Error creating project:', err);
        const msg =
          err?.message ||
          err?.error ||
          'Failed to create project. Please try again.';

        // ✅ ONE error system: AlertBox
        setAlert(msg, 'error');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };


  const handleGoToProject = () => {
    if (newProjectId) {
      navigate(`/dashboard/project/${newProjectId}`);
    }
  };

 return (
  <div className="cp-page">
    <div className="cp-card">
      {/* Header + Stepper */}
      <div className="cp-head">
        <div>
          <h2 className="cp-title">Create Project</h2>
          <p className="cp-subtitle">Set up a new site in under 1 minute.</p>
        </div>

        <div className="cp-stepper">
          <div className={`cp-step ${step >= 1 ? 'active' : ''}`}>
            <span>1</span> Basics
          </div>
          <div className={`cp-step ${step >= 2 ? 'active' : ''}`}>
            <span>2</span> Schedule
          </div>
          <div className={`cp-step ${step >= 3 ? 'active' : ''}`}>
            <span>3</span> Done
          </div>
        </div>
      </div>

      <div className="cp-divider" />

      <AnimatePresence mode="wait">
        {/* ===================== STEP 1 ===================== */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.35 }}
            className="cp-step-body"
          >
            <AlertBox
              type={localAlert.type}
              message={localAlert.message}
              onClose={clearAlert}
            />

            <div className="cp-grid">
              <div className="cp-field full">
                <label className="cp-label">Project / Site Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter project name"
                  value={formData.name}
                  onChange={handleChange}
                  className="cp-input"
                />
                {errors.name && <p className="cp-error">{errors.name}</p>}
              </div>

              <div className="cp-field">
                <label className="cp-label">Client Name</label>
                <input
                  type="text"
                  name="client"
                  placeholder="Enter client name"
                  value={formData.client}
                  onChange={handleChange}
                  className="cp-input"
                />
                {errors.client && <p className="cp-error">{errors.client}</p>}
              </div>

              <div className="cp-field">
                <label className="cp-label">Project / Site Location</label>
                <input
                  type="text"
                  name="location"
                  placeholder="Enter project location"
                  value={formData.location}
                  onChange={handleChange}
                  className="cp-input"
                />
                {errors.location && <p className="cp-error">{errors.location}</p>}
              </div>

              <div className="cp-field full">
                <label className="cp-label">Project / Site Image (optional)</label>

                <div
                  className="cp-upload"
                  onClick={() => {
                    if (!isUploadingImage) document.getElementById('fileInput').click();
                  }}
                >
                  {isUploadingImage && (
                    <div className="cp-upload-overlay">
                      <div className="cp-spinner" />
                      <div>
                        {uploadStatusText || 'Uploading...'}{' '}
                        {uploadProgress > 0 && <b>{uploadProgress}%</b>}
                      </div>
                    </div>
                  )}

                  {!isUploadingImage && (
                    <>
                      {thumbnail ? (
                        <img src={thumbnail} alt="Preview" className="cp-thumb" />
                      ) : (
                        <span className="cp-upload-text">+ Upload thumbnail</span>
                      )}
                    </>
                  )}
                </div>

                <input
                  id="fileInput"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                {thumbnail && !isUploadingImage && (
                  <p className="cp-hint">✓ Image uploaded</p>
                )}
              </div>
            </div>

            <div className="cp-actions">
              <button className="cp-primary" onClick={handleNext}>
                Continue <FaChevronRight />
              </button>
            </div>
          </motion.div>
        )}

        {/* ===================== STEP 2 ===================== */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.35 }}
            className="cp-step-body"
          >
            <AlertBox
              type={localAlert.type}
              message={localAlert.message}
              onClose={clearAlert}
            />

            <div className="cp-grid">
              <div className="cp-field">
                <label className="cp-label">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="cp-input"
                  min={todayISO}
                />
                {formData.startDate && (
                  <p className="cp-hint">
                    Starts on <b>{prettyDate(formData.startDate)}</b>
                  </p>
                )}
                {errors.startDate && <p className="cp-error">{errors.startDate}</p>}
              </div>

              <div className="cp-field">
                <label className="cp-label">End Date (Estimate)</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="cp-input"
                  min={formData.startDate || todayISO}
                />
                {formData.endDate && (
                  <p className="cp-hint">
                    Estimated completion: <b>{prettyDate(formData.endDate)}</b>
                  </p>
                )}
                {errors.endDate && <p className="cp-error">{errors.endDate}</p>}
              </div>

              <div className="cp-field full">
                <label className="cp-label">Quick duration</label>
                <div className="cp-quick-row">
                  <button
                    type="button"
                    className="cp-pill"
                    onClick={() => handleQuickDuration(3)}
                  >
                    3 months
                  </button>
                  <button
                    type="button"
                    className="cp-pill"
                    onClick={() => handleQuickDuration(6)}
                  >
                    6 months
                  </button>
                  <button
                    type="button"
                    className="cp-pill"
                    onClick={() => handleQuickDuration(12)}
                  >
                    12 months
                  </button>
                </div>
              </div>

              <div className="cp-field full">
                <label className="cp-label">Budget (₹)</label>
                <input
                  type="text"
                  name="budget"
                  placeholder="e.g. 500000"
                  value={formData.budget}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, '');
                    setFormData((prev) => ({ ...prev, budget: val }));
                  }}
                  className="cp-input"
                />
                {errors.budget && <p className="cp-error">{errors.budget}</p>}
              </div>
            </div>

            <div className="cp-actions">
              <button
                type="button"
                className="cp-secondary"
                onClick={() => {
                  clearAlert();
                  setErrors({});
                  setStep(1);
                }}
                disabled={isSubmitting}
              >
                Back
              </button>

              <button
                type="button"
                className="cp-primary"
                onClick={saveProject}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating…' : 'Create Project'}
              </button>
            </div>
          </motion.div>
        )}

        {/* ===================== STEP 3 (SUCCESS) ===================== */}
        {step === 3 && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.35 }}
            className="cp-success"
          >
            <motion.img
              src={SuccessImage}
              alt="Success"
              initial={{ scale: 0.85 }}
              animate={{ scale: [0.9, 1.05, 1] }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
            />

            <h2>Project created successfully 🎉</h2>

            <p>
              <b>{formData.name || 'Your project'}</b> is ready. Next, add labour,
              materials and start logging daily progress from the project dashboard.
            </p>

            <div className="cp-success-actions">
              <button className="cp-primary" onClick={handleGoToProject}>
                Go to project dashboard
              </button>

              <button
                type="button"
                className="cp-secondary"
                onClick={() => navigate('/dashboard/projects')}
              >
                Back to all projects
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </div>
);

};

export default CreateProject;
